// src/pages/admin/adminPageEditor/utils/editorUtils.js

export const deepClone = (obj) => JSON.parse(JSON.stringify(obj || {}));

export function normalizeHexColor(value, fallback = "#ffffff") {
  const v = String(value || "").trim();

  // Accept only full hex format: #rrggbb
  if (/^#[0-9A-Fa-f]{6}$/.test(v)) {
    return v.toLowerCase();
  }

  return fallback;
}

export function mergeSectionPatch(section, patch) {
  const next = { ...section, ...patch };
  if (patch.config) next.config = { ...(section.config || {}), ...patch.config };
  if (patch.style) next.style = { ...(section.style || {}), ...patch.style };
  return next;
}

export function mergeBlockPatch(prev, patch) {
  const next = { ...(prev || {}), ...(patch || {}) };
  if (patch?.config) next.config = { ...((prev && prev.config) || {}), ...patch.config };
  if (patch?.style) next.style = { ...((prev && prev.style) || {}), ...patch.style };
  return next;
}

export function resolveSectionTypeKey(rawType, templatesObj) {
  const t = (rawType || "").trim();
  if (!t) return t;

  const candidates = [
    t,
    t.toLowerCase(),

    t === "categorySection" ? "categorysection" : null,
    t === "categorysection" ? "categorySection" : null,

    t === "locationSection" ? "locationsection" : null,
    t === "locationsection" ? "locationSection" : null,

    t === "serviceSection" ? "servicesection" : null,
    t === "servicesection" ? "serviceSection" : null,
  ].filter(Boolean);

  return candidates.find((k) => templatesObj?.[k]) || t;
}

export function buildTemplatePatch(section, templateKey, sectionTemplates) {
  const typeKey = resolveSectionTypeKey(section?.type, sectionTemplates);
  const tpl = sectionTemplates?.[typeKey]?.[templateKey];

  const defaultConfig = deepClone(tpl?.config || {});
  const defaultStyle = deepClone(tpl?.style || {});

  const currentConfig = section?.config || {};
  const currentStyle = section?.style || {};

  const nextConfig = { ...defaultConfig, ...currentConfig };
  const nextStyle = { ...defaultStyle, ...currentStyle };

  if (!Object.prototype.hasOwnProperty.call(nextStyle, "bgColor")) {
    nextStyle.bgColor = "";
  }

  return { template: templateKey, config: nextConfig, style: nextStyle };
}

export function buildGeneralPayload(general) {
  const lang = general?.language || "en";
  const dir =
    general?.direction ||
    (lang === "ar" || lang === "he" ? "rtl" : "ltr");

  return {
    phone: general?.phone || "",
    city: general?.city || "",
    logoUrl: general?.logoUrl || "",
    scrollAnim: general?.scrollAnim || "fadeUp",
    pageBgColor: normalizeHexColor(general?.pageBgColor, "#ffffff"),
    scrollOnce: typeof general?.scrollOnce === "boolean" ? general.scrollOnce : true,
    scrollBehavior: general?.scrollBehavior || "smooth",
    language: lang,
    direction: dir,
  };
}

export function hydrateEditorState(found, sectionTemplates, normalizeLimitsFn) {
  const limits = normalizeLimitsFn(found?.limits || {});

  const general = {
    phone: found?.general?.phone || "",
    city: found?.general?.city || "",
    logoUrl: found?.general?.logoUrl || "",
    pageBgColor: normalizeHexColor(found?.general?.pageBgColor, "#ffffff"),
    scrollAnim: found?.general?.scrollAnim || "fadeUp",
    scrollOnce:
      typeof found?.general?.scrollOnce === "boolean"
        ? found.general.scrollOnce
        : true,
    scrollBehavior: found?.general?.scrollBehavior || "smooth",
    language: found?.general?.language || "en",
    direction:
      found?.general?.direction ||
      (found?.general?.language === "ar" || found?.general?.language === "he"
        ? "rtl"
        : "ltr"),
  };

  const whatsappBase = found?.whatsapp || {};
  const whatsapp = {
    enabled: whatsappBase.enabled ?? false,
    template: whatsappBase.template || "template1",
    config: {
      phone: whatsappBase.config?.phone || "",
      message: whatsappBase.config?.message || "Hello, I want more info",
    },
    style: {
      position: whatsappBase.style?.position || "right",
    },
  };

  const navTpl = sectionTemplates?.navsection?.template1;
  const nav =
    found?.nav || {
      enabled: true,
      template: "template1",
      config: deepClone(navTpl?.config || {}),
      style: { ...(deepClone(navTpl?.style || {})), bgColor: navTpl?.style?.bgColor ?? "" },
    };

  const footerTpl = sectionTemplates?.footersection?.template1;
  const footer =
    found?.footer || {
      enabled: true,
      template: "template1",
      config: deepClone(footerTpl?.config || {}),
      style: { ...(deepClone(footerTpl?.style || {})), bgColor: footerTpl?.style?.bgColor ?? "" },
    };

  const sections = (found?.sections || []).map((s) => ({
    ...s,
    style: {
      ...(s.style || {}),
      bgColor: s?.style?.bgColor ?? "",
    },
  }));

  return {
    pageName: found?.name || "",
    pageSlug: found?.slug || "",
    general,
    limits,
    whatsapp,
    nav,
    footer,
    sections,
  };
}